/**
 * Results overlay (DESIGN 4.6, 6.6 step 5): the `end` message as a table of
 * kills, damage, healing, the friendly-fire "Oops" meter with the Menace
 * title, deaths and revives, plus the wave reached and the team score. The
 * server sends the room back to the lobby after RESULTS_SECONDS; the
 * overlay counts that down and also hides itself the moment the phase
 * leaves `gameover`, or when the button is pressed. The button is a ready
 * press (`next`, 2026-10-28): alone it goes back to the lobby at once, with
 * friends once everyone has pressed it (before, it only hid the panel and
 * Start stayed locked for the full RESULTS_SECONDS).
 */

import type { MatchResult } from '@shared/index.js';
import { FINAL_WAVE, RESULTS_SECONDS } from '@shared/index.js';
import type { ClientState } from '../state/clientState.js';
import type { Socket } from '../net/socket.js';
import { type Screen, button, clear, el, focusPanel, setText } from './dom.js';
import { MENACE_TITLE, type ResultRow, buildResultRows, formatInt, menaceOf, resultHeadline, resultSubline } from './helpers.js';
import { TEAMS } from '../palette.js';

export function mount(root: HTMLElement, state: ClientState, socket: Socket): Screen & { readonly shown: boolean } {
  const headline = el('h2', { class: 'results-headline' });
  const subline = el('div', { class: 'results-sub' });
  const menaceLine = el('div', { class: 'results-menace' });
  const tbody = el('tbody');
  const countdown = el('div', { class: 'results-countdown', 'aria-live': 'polite' });
  const backBtn = button('Back to lobby', () => {
    dismissed = true;
    socket.send({ t: 'next' });
  }, 'btn btn-primary');

  const title = el('div', { class: 'results-title' }, 'Game over');
  const panel = el('div', { class: 'results-panel', role: 'dialog', 'aria-label': 'Match results', tabindex: -1 },
    title,
    headline,
    subline,
    menaceLine,
    el('table', { class: 'results-table' },
      el('thead', null, el('tr', null,
        el('th', { class: 'left' }, 'Wizard'), el('th', null, 'Kills'), el('th', null, 'Damage'), el('th', null, 'Healing'),
        el('th', { class: 'left' }, 'Oops'), el('th', null, 'Deaths'), el('th', null, 'Revives'),
      )),
      tbody,
    ),
    el('div', { class: 'results-foot' }, countdown, backBtn),
  );
  clear(root);
  root.classList.add('results');
  root.appendChild(panel);

  let shown: MatchResult | null = null;
  let shownAt = 0;
  let dismissed = false;
  let suppressed = false;
  let lastSecond = -1;

  function rowNode(r: ResultRow, me: boolean): HTMLElement {
    const meter = el('div', { class: 'oops-meter', title: `${formatInt(r.ff)} friendly-fire damage` },
      el('div', { class: 'oops-fill' }),
      el('span', { class: 'oops-num' }, formatInt(r.ff)),
    );
    (meter.firstElementChild as HTMLElement).style.width = `${Math.round(r.oops * 100)}%`;
    let dot: HTMLElement | null = null;
    if (r.team !== undefined) {
      dot = el('span', { class: 'team-dot', title: TEAMS[r.team].name });
      dot.style.setProperty('--team', TEAMS[r.team].color);
    }
    return el('tr', { class: `${me ? 'row-me' : ''}${r.menace ? ' row-menace' : ''}` },
      el('td', { class: 'left' }, dot, r.name, r.menace ? el('span', { class: 'badge badge-menace' }, MENACE_TITLE) : null),
      el('td', null, formatInt(r.kills)),
      el('td', null, formatInt(r.dmg)),
      el('td', null, formatInt(r.heal)),
      el('td', { class: 'left' }, meter),
      el('td', null, formatInt(r.deaths)),
      el('td', null, formatInt(r.revives)),
    );
  }

  function render(result: MatchResult): void {
    setText(title, result.mode === 'story' ? (result.won ? 'Act 1: The Drowned Cloister, done' : 'Act 1') : result.mode === 'ctf' ? 'Capture the flag' : 'Game over');
    setText(headline, resultHeadline(result, FINAL_WAVE));
    setText(subline, resultSubline(result));
    const menace = menaceOf(result);
    setText(menaceLine, menace ? `${menace.name} is the ${MENACE_TITLE}: most damage to friends.` : 'Nobody hurt a friend. Suspicious.');
    clear(tbody);
    for (const r of buildResultRows(result)) tbody.appendChild(rowNode(r, r.id === state.pid));
  }

  const unsubscribe = state.subscribe((vm) => {
    const result = state.lastResult;
    if (result && result !== shown && vm.phase === 'gameover' && !suppressed) {
      shown = result;
      shownAt = performance.now();
      dismissed = false;
      lastSecond = -1;
      render(result);
      root.hidden = false;
      // Focus the panel, not its button: a player still mashing Space or Enter must not dismiss it by accident.
      focusPanel(panel);
    }
    if (root.hidden) return;
    const elapsed = (performance.now() - shownAt) / 1000;
    const left = Math.max(0, Math.ceil(RESULTS_SECONDS - elapsed));
    if (vm.phase !== 'gameover' || dismissed || suppressed || left === 0) {
      root.hidden = true;
      return;
    }
    if (left !== lastSecond) {
      lastSecond = left;
      setText(countdown, `Back to the lobby in ${left} s`);
    }
  });

  root.hidden = true;
  return {
    get shown(): boolean { return !root.hidden; },
    show(): void { suppressed = false; },
    hide(): void { suppressed = true; root.hidden = true; },
    dispose(): void {
      unsubscribe();
      clear(root);
    },
  };
}
